import React, { useEffect, useState } from 'react'
import _ from 'lodash';
import ToastMessage from '../../../../common/ToastMessage';
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useApprovalPersons from '../../../../store/ReactQueries/procurements/approvalPersons/useApprovalPersons';
import useAddApprovalPerson from '../../../../store/ReactQueries/procurements/approvalPersons/useAddApprovalPerson';
import useUpdateApprovalPerson from '../../../../store/ReactQueries/procurements/approvalPersons/useUpdateApprovalPerson';
import useDeleteApprovalPerson from '../../../../store/ReactQueries/procurements/approvalPersons/useDeleteApprovalPerson';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../utils/constants';
import { FieldErrors } from '../../../../domain/helpers/Errors';
import { validateSchema } from '../../../../common/ValidateSchema';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { ApprovalPersonInterface } from '../../../../models/ProcurementModule/ApprovalPerson';
import { approvalPersonSchema } from '../../../../domain/ValidationSchemas/PurchaseOrder/approvalPerson';

export const ApprovalPerson = () => {
    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});

    const initialValue: ApprovalPersonInterface = {
        id: '',
        userId: '',
        minValue: 0,
        maxValue: 0,
        order: 0,
        purchaseOrderTypeId: '',
        purchaseOrderType: { id: '', title: '' },
        user: { firstname: '', lastname: '', email: '' }
    }

    const [form, setForm] = useState<ApprovalPersonInterface>(initialValue);
    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });
    let { data: approvalPersons, error: approvalPersonError, isLoading } = useApprovalPersons(page, pageSize);

    const addApprovalPerson = useAddApprovalPerson(page, pageSize);
    const updateApprovalPerson = useUpdateApprovalPerson(page, pageSize);
    const deleteApprovalPerson = useDeleteApprovalPerson(page, pageSize);


    useEffect(() => {
        if (approvalPersonError) ToastMessage.Failed(`Error: ${approvalPersonError.message}`);
    }, [approvalPersonError])

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false },
        {
            field: 'user',
            headerName: 'User',
            type: "string",
            editable: false,
            width: 200,
            renderCell: ({ row }) => (
                <span>
                    {row.user
                        ? `${row.user?.firstname ?? ""} ${row.user?.lastname ?? ""}`
                        : ""}
                </span>
            ),
        },
        {
            field: 'purchaseOrderType',
            headerName: 'Purchase Order Type',
            type: "string",
            editable: false,
            width: 200,
            renderCell: ({ row }) => (
                <span>
                    {row.purchaseOrderType
                        ? row.purchaseOrderType?.title ?? ""
                        : ""}
                </span>
            )
        },
        { field: 'order', headerName: 'Order', type: "number", editable: false },
        { field: 'minValue', headerName: 'Min', type: "number", editable: false },
        { field: 'maxValue', headerName: 'Max', type: "number", editable: false },
        { field: 'purchaseOrderTypeId', headerName: 'PurchaseOrderTypeId', type: "string", editable: false },
        { field: 'userId', headerName: 'UserId', type: "string", editable: false },
    ];

    const addItems = () => {
        setForm(initialValue);
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
    };

    const handleSave = async () => {
        const _form = { ...form };
        const isAdd = _form.id === '';

        const itemByPurchaseOrderType = approvalPersons?.filter(item => item.purchaseOrderTypeId === _form.purchaseOrderTypeId);
        if (itemByPurchaseOrderType.length === 0) {
            _form.order = 1;
        } else {
            if (itemByPurchaseOrderType.length) {
                const errorsCopy = { ...errors };

                const orderExists = isAdd
                    ? itemByPurchaseOrderType.some(item => item.order === _form.order)
                    : itemByPurchaseOrderType.some(item => item.order === _form.order && item.id !== _form.id);

                if (orderExists) errorsCopy.order = "Order exists!";

                const userExists = isAdd
                    ? itemByPurchaseOrderType.some(item => item.userId === _form.userId)
                    : itemByPurchaseOrderType.some(item => item.userId === _form.userId && item.id !== _form.id);

                if (userExists) errorsCopy.userId = 'User already exists in this Purchase Order Type';

                if (Object.keys(errorsCopy).length > 0) {
                    setErrors(errorsCopy);
                    return;
                }
            }
        }

        const isValid = validateSchema(approvalPersonSchema, form);

        setErrors({ ...errors, ...isValid.errors });
        if (!isValid.result) return;

        const { id, userId, minValue, maxValue, order, purchaseOrderTypeId } = _form;

        const upForm: ApprovalPersonInterface = {
            id,
            userId,
            minValue,
            maxValue,
            order,
            purchaseOrderTypeId,
        };

        if (isAdd) {
            let { id, ...formData } = upForm;
            addApprovalPerson.mutate(formData, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        } else {
            updateApprovalPerson.mutate(upForm, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deleteApprovalPerson.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language['ApprovalPerson']}
            rows={approvalPersons || []}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            loading={isLoading}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            addItems={addItems}
            setModal={setModal}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
            hideColumns={['id', 'purchaseOrderTypeId', 'userId']}
            showColumnModal={['user', 'purchaseOrderTypeId', 'order', 'minValue', 'maxValue']}
        />
    );
}
export default ApprovalPerson;